import { useEffect, useMemo, useState } from 'react';
import { Cell, Label, Pie, PieChart } from 'recharts';
import type { RouteDefinition } from '@/routes';
import { applyRouteMeta } from '@/lib/meta';
import { AppLayout } from '@/layout/AppLayout';
import { AssumptionPanel } from '@/components/calculator/AssumptionPanel';
import { CalculatorSeoArticle } from '@/components/calculator/CalculatorSeoArticle';
import { ChartCard } from '@/components/calculator/ChartCard';
import { FieldExplainer } from '@/components/calculator/FieldExplainer';
import { FieldExplainerGuide } from '@/components/calculator/FieldExplainerGuide';
import { NumberField } from '@/components/calculator/NumberField';
import { ResultSummaryCard } from '@/components/calculator/ResultSummaryCard';
import { ShareExportRow } from '@/components/calculator/ShareExportRow';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { Select } from '@/components/ui/select';
import { ChartContainer, ChartLegendPills, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { getPaycheckFieldExplainers } from '@/content/field-explainers/paycheck';
import type { FieldExplainerContent } from '@/content/field-explainers/types';
import { calculatePaycheck, type PaycheckInput } from '@/engines/legacy-engines';
import {
  copyToClipboard,
  createContractShareUrl,
  exportPdfReport,
  exportRowsToCsv,
  formatCurrency,
  parseContractState
} from './shared';

type PaycheckState = {
  salary: number;
  payFrequency: PaycheckInput['payFrequency'];
  filingStatus: PaycheckInput['filingStatus'];
  state: number;
  contribution401kPercent: number;
  healthInsurance: number;
  hsa: number;
  otherPreTax: number;
};

const defaults: PaycheckState = {
  salary: 75000,
  payFrequency: 'biweekly',
  filingStatus: 'single',
  state: 9.3,
  contribution401kPercent: 6,
  healthInsurance: 150,
  hsa: 0,
  otherPreTax: 0
};

const chartConfig = {
  pretax: {
    label: 'Pre-tax deductions',
    color: 'var(--chart-2)'
  },
  taxes: {
    label: 'Taxes',
    color: 'var(--chart-5)'
  },
  net: {
    label: 'Net pay',
    color: 'var(--chart-1)'
  }
} satisfies ChartConfig;

type Props = { route: RouteDefinition };

function toEngineInput(state: PaycheckState): PaycheckInput {
  return {
    salary: state.salary,
    payFrequency: state.payFrequency,
    filingStatus: state.filingStatus,
    stateRate: state.state,
    contribution401kPercent: state.contribution401kPercent,
    healthInsurance: state.healthInsurance,
    hsa: state.hsa,
    otherPreTax: state.otherPreTax
  };
}

export function PaycheckCalculatorPage({ route }: Props) {
  const { toast } = useToast();
  const [state, setState] = useState<PaycheckState>(() => {
    const parsed = parseContractState('paycheck', defaults);
    return {
      ...parsed,
      state: Number(parsed.state ?? defaults.state)
    };
  });

  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const result = useMemo(() => calculatePaycheck(toEngineInput(state)), [state]);

  const explainers = useMemo(() => {
    const entries = getPaycheckFieldExplainers({
      input: {
        salary: state.salary,
        payFrequency: state.payFrequency,
        filingStatus: state.filingStatus,
        stateRate: state.state,
        contribution401kPercent: state.contribution401kPercent,
        healthInsurance: state.healthInsurance,
        hsa: state.hsa,
        otherPreTax: state.otherPreTax
      },
      result: {
        periodsPerYear: result.periodsPerYear,
        grossPayPerPeriod: result.grossPayPerPeriod,
        contribution401kAnnual: result.contribution401kAnnual,
        contribution401kPerPeriod: result.contribution401kPerPeriod,
        preTaxDeductionsPerPeriod: result.preTaxDeductionsPerPeriod,
        taxableIncomeAnnual: result.taxableIncomeAnnual,
        standardDeduction: result.standardDeduction,
        federalTaxableIncomeAnnual: result.federalTaxableIncomeAnnual,
        federalMarginalRate: result.federalMarginalRate,
        federalBracketLowerBound: result.federalBracketLowerBound,
        federalBracketUpperBound: result.federalBracketUpperBound,
        federalTaxAnnual: result.federalTaxAnnual,
        federalTaxPerPeriod: result.federalTaxPerPeriod,
        stateTaxAnnual: result.stateTaxAnnual,
        stateTaxPerPeriod: result.stateTaxPerPeriod,
        socialSecurityAnnual: result.socialSecurityAnnual,
        socialSecurityPerPeriod: result.socialSecurityPerPeriod,
        medicareAnnual: result.medicareAnnual,
        medicarePerPeriod: result.medicarePerPeriod,
        additionalMedicareThreshold: result.additionalMedicareThreshold,
        totalTaxesPerPeriod: result.totalTaxesPerPeriod,
        netPayPerPeriod: result.netPayPerPeriod,
        annualNetPay: result.annualNetPay
      }
    });
    const byId = new Map<string, FieldExplainerContent>(entries.map((entry) => [entry.id, entry]));
    return { entries, byId };
  }, [state, result]);

  const deductionRows = [
    { label: 'Federal tax', perPeriod: result.federalTaxPerPeriod, annual: result.federalTaxAnnual },
    { label: 'State tax', perPeriod: result.stateTaxPerPeriod, annual: result.stateTaxAnnual },
    { label: 'Social Security', perPeriod: result.socialSecurityPerPeriod, annual: result.socialSecurityAnnual },
    { label: 'Medicare', perPeriod: result.medicarePerPeriod, annual: result.medicareAnnual },
    { label: '401(k)', perPeriod: result.contribution401kPerPeriod, annual: result.contribution401kAnnual },
    { label: 'Health insurance', perPeriod: state.healthInsurance, annual: state.healthInsurance * result.periodsPerYear },
    { label: 'HSA', perPeriod: state.hsa, annual: state.hsa * result.periodsPerYear },
    { label: 'Other pre-tax', perPeriod: state.otherPreTax, annual: state.otherPreTax * result.periodsPerYear }
  ];

  const chartData = [
    { key: 'pretax', label: 'Pre-tax', value: result.preTaxDeductionsPerPeriod },
    { key: 'taxes', label: 'Taxes', value: result.totalTaxesPerPeriod },
    { key: 'net', label: 'Net', value: result.netPayPerPeriod }
  ];

  return (
    <AppLayout>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <h1 className="font-display text-4xl leading-tight md:text-5xl">Paycheck Calculator</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
          Estimate net pay by combining taxes, deductions, and contribution settings across different pay schedules.
        </p>
      </section>

      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="before" />

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <AssumptionPanel title="Income and withholding setup" description="Configure gross salary, filing status, pay cadence, and pre-tax deductions.">
          <div className="grid gap-4 md:grid-cols-2">
            <NumberField id="paySalary" label="Annual salary" value={state.salary} min={0} step={500} prefix="$" explainer={explainers.byId.get('salary')} onChange={(value) => setState((prev) => ({ ...prev, salary: value }))} />
            <NumberField id="payStateRate" label="State tax rate" value={state.state} min={0} max={15} step={0.1} suffix="%" explainer={explainers.byId.get('stateRate')} onChange={(value) => setState((prev) => ({ ...prev, state: value }))} />
            <NumberField id="pay401k" label="401(k) contribution" value={state.contribution401kPercent} min={0} max={50} step={0.5} suffix="%" explainer={explainers.byId.get('contribution401k')} onChange={(value) => setState((prev) => ({ ...prev, contribution401kPercent: value }))} />
            <NumberField id="payHealth" label="Health insurance per pay period" value={state.healthInsurance} min={0} step={10} prefix="$" explainer={explainers.byId.get('healthInsurance')} onChange={(value) => setState((prev) => ({ ...prev, healthInsurance: value }))} />
            <NumberField id="payHsa" label="HSA per pay period" value={state.hsa} min={0} step={10} prefix="$" explainer={explainers.byId.get('hsa')} onChange={(value) => setState((prev) => ({ ...prev, hsa: value }))} />
            <NumberField id="payOtherPretax" label="Other pre-tax per pay period" value={state.otherPreTax} min={0} step={10} prefix="$" explainer={explainers.byId.get('otherPreTax')} onChange={(value) => setState((prev) => ({ ...prev, otherPreTax: value }))} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2 text-sm" htmlFor="paycheck-pay-frequency">
              <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                Pay frequency
                {explainers.byId.get('payFrequency') ? <FieldExplainer explainer={explainers.byId.get('payFrequency')!} /> : null}
              </span>
              <Select id="paycheck-pay-frequency" value={state.payFrequency} onChange={(event) => setState((prev) => ({ ...prev, payFrequency: event.target.value as PaycheckState['payFrequency'] }))} options={[{ value: 'weekly', label: 'Weekly' }, { value: 'biweekly', label: 'Biweekly' }, { value: 'semimonthly', label: 'Semimonthly' }, { value: 'monthly', label: 'Monthly' }]} />
            </label>
            <label className="space-y-2 text-sm" htmlFor="paycheck-filing-status">
              <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                Filing status
                {explainers.byId.get('filingStatus') ? <FieldExplainer explainer={explainers.byId.get('filingStatus')!} /> : null}
              </span>
              <Select id="paycheck-filing-status" value={state.filingStatus} onChange={(event) => setState((prev) => ({ ...prev, filingStatus: event.target.value as PaycheckState['filingStatus'] }))} options={[{ value: 'single', label: 'Single' }, { value: 'married', label: 'Married' }, { value: 'head', label: 'Head of household' }]} />
            </label>
          </div>
        </AssumptionPanel>

        <div className="space-y-4">
          <ResultSummaryCard label="Net pay per period" value={formatCurrency(result.netPayPerPeriod)} note={`${result.periodsPerYear} pay periods per year`} trend="up" explainer={explainers.byId.get('netPayPerPeriod')} />
          <ResultSummaryCard label="Annual net pay" value={formatCurrency(result.annualNetPay)} note="after estimated taxes and pre-tax deductions" trend="flat" explainer={explainers.byId.get('annualNetPay')} />
          <ResultSummaryCard label="Total taxes per period" value={formatCurrency(result.totalTaxesPerPeriod)} note="federal + state + FICA" trend="down" explainer={explainers.byId.get('totalTaxesPerPeriod')} />
          <ResultSummaryCard label="Gross pay per period" value={formatCurrency(result.grossPayPerPeriod)} note="before deductions" trend="flat" explainer={explainers.byId.get('grossPayPerPeriod')} />

          <ShareExportRow
            onCopyShare={async () => {
              const shareUrl = createContractShareUrl(route.canonicalPath, 'paycheck', state as Record<string, unknown>);
              const ok = await copyToClipboard(shareUrl);
              toast({ title: ok ? 'Share link copied' : 'Share link ready', description: ok ? shareUrl : `Copy manually: ${shareUrl}`, variant: ok ? 'success' : 'warning' });
            }}
            onExportCsv={() => {
              const headers = ['Deduction', 'Per Period', 'Annual'];
              const rows = deductionRows.map((row) => [row.label, Math.round(row.perPeriod), Math.round(row.annual)]);
              exportRowsToCsv(headers, rows, 'paycheck-deduction-breakdown.csv');
              toast({ title: 'CSV exported', description: 'paycheck-deduction-breakdown.csv downloaded.', variant: 'success' });
            }}
            onExportPdf={async () => {
              const ok = await exportPdfReport({
                title: 'Paycheck Breakdown',
                subtitle: `${state.payFrequency} pay frequency`,
                summaryLeft: [
                  `Annual salary: ${formatCurrency(state.salary)}`,
                  `Filing status: ${state.filingStatus}`,
                  `State tax rate: ${state.state.toFixed(1)}%`,
                  `401(k) contribution: ${state.contribution401kPercent.toFixed(1)}%`
                ],
                summaryRight: [
                  `Gross per period: ${formatCurrency(result.grossPayPerPeriod)}`,
                  `Pre-tax deductions: ${formatCurrency(result.preTaxDeductionsPerPeriod)}`,
                  `Taxes per period: ${formatCurrency(result.totalTaxesPerPeriod)}`,
                  `Net per period: ${formatCurrency(result.netPayPerPeriod)}`
                ],
                headers: ['Category', 'Per Period', 'Annual'],
                rows: deductionRows.map((row) => [row.label, formatCurrency(row.perPeriod), formatCurrency(row.annual)]),
                filename: 'paycheck-deduction-breakdown.pdf'
              });

              toast({
                title: ok ? 'PDF exported' : 'PDF export unavailable',
                description: ok ? 'paycheck-deduction-breakdown.pdf downloaded.' : 'PDF dependencies are not available right now.',
                variant: ok ? 'success' : 'warning'
              });
            }}
          />
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <ChartCard
          title="Per-period paycheck composition"
          description="Gross pay split into pre-tax deductions, taxes, and net pay."
          annotation={
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span>
                Gross pay <span className="font-semibold text-foreground">{formatCurrency(result.grossPayPerPeriod)}</span>
              </span>
              <span>
                Net pay <span className="font-semibold text-foreground">{formatCurrency(result.netPayPerPeriod)}</span>
              </span>
            </div>
          }
          legend={<ChartLegendPills config={chartConfig} keys={['pretax', 'taxes', 'net']} />}
        >
          <ChartContainer config={chartConfig} family="allocation">
            <PieChart accessibilityLayer>
              <Pie data={chartData} dataKey="value" nameKey="label" innerRadius={60} outerRadius={95} strokeWidth={2}>
                <Label
                  content={({ viewBox }) => {
                    if (!viewBox || !('cx' in viewBox) || !('cy' in viewBox)) {
                      return null;
                    }

                    return (
                      <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                        <tspan x={viewBox.cx} y={viewBox.cy - 10} className="fill-muted-foreground text-[11px] uppercase tracking-[0.16em]">
                          Net pay
                        </tspan>
                        <tspan x={viewBox.cx} y={viewBox.cy + 14} className="fill-foreground font-display text-[20px]">
                          {formatCurrency(result.netPayPerPeriod)}
                        </tspan>
                      </text>
                    );
                  }}
                />
                {chartData.map((entry) => (
                  <Cell key={entry.key} fill={`var(--color-${entry.key})`} />
                ))}
              </Pie>
              <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel formatter={(value) => formatCurrency(Number(value ?? 0))} />} />
            </PieChart>
          </ChartContainer>
        </ChartCard>

        <ChartCard title="Paycheck composition" description="Key paycheck buckets per pay period.">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bucket</TableHead>
                <TableHead numeric>Per period</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>Gross pay</TableCell>
                <TableCell numeric>{formatCurrency(result.grossPayPerPeriod)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Pre-tax deductions</TableCell>
                <TableCell numeric>{formatCurrency(result.preTaxDeductionsPerPeriod)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Taxes</TableCell>
                <TableCell numeric>{formatCurrency(result.totalTaxesPerPeriod)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Net pay</TableCell>
                <TableCell numeric>{formatCurrency(result.netPayPerPeriod)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </ChartCard>
      </section>

      <section>
        <ChartCard title="Deduction details" description="Per-period and annual estimates by deduction category.">
          <Table containerClassName="max-h-[28rem]" stickyHeader compact>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead numeric>Per period</TableHead>
                <TableHead numeric>Annual</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deductionRows.map((row) => (
                <TableRow key={row.label}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell numeric>{formatCurrency(row.perPeriod)}</TableCell>
                  <TableCell numeric>{formatCurrency(row.annual)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ChartCard>
      </section>

      <section>
        <FieldExplainerGuide
          title="How each number is calculated"
          description="Every input, tax line, and result on this page, worked through with your own figures. Tap the info icon beside any field for the same explanation in place."
          entries={explainers.entries}
        />
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="after" />
    </AppLayout>
  );
}
